import { useState } from "react";
import type { VoteQuestion } from "@/lib/types";

export interface AnswerValue {
  opts: number[];
  text: string;
  score: number;
}

interface Props {
  index: number;
  question: VoteQuestion;
  value: AnswerValue;
  onChange: (next: AnswerValue) => void;
}

// 질문 1개 렌더링 (single / multi / text / scale)
export default function VoteQuestionBlock({
  index,
  question,
  value,
  onChange,
}: Props) {
  const [limitHit, setLimitHit] = useState(false);
  const qtype = question.qtype;
  const maxPick = question.max_pick ?? 0;
  const options = question.options ?? [];

  const toggleOption = (optNo: number) => {
    if (qtype === "single") {
      onChange({ ...value, opts: [optNo] });
      return;
    }
    const has = value.opts.includes(optNo);
    if (has) {
      setLimitHit(false);
      onChange({ ...value, opts: value.opts.filter((o) => o !== optNo) });
      return;
    }
    if (maxPick > 0 && value.opts.length >= maxPick) {
      setLimitHit(true);
      return;
    }
    setLimitHit(false);
    onChange({ ...value, opts: [...value.opts, optNo] });
  };

  return (
    <li className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
          {index + 1}
        </span>
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
          {question.title}
          {question.is_req === 1 && (
            <span className="ml-1 text-accent-600" aria-label="필수">
              *
            </span>
          )}
        </h3>
      </div>

      {qtype === "multi" && maxPick > 0 && (
        <p className="mt-2 pl-8 text-xs text-foreground-500">
          최대 {maxPick}개까지 선택할 수 있습니다.
        </p>
      )}

      {(qtype === "single" || qtype === "multi") && (
        <div className="mt-3 space-y-2">
          {options.map((opt) => {
            const selected = value.opts.includes(opt.no);
            return (
              <button
                key={opt.no}
                type="button"
                onClick={() => toggleOption(opt.no)}
                aria-pressed={selected}
                className={`flex min-h-[48px] w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                  selected
                    ? "border-primary-400 bg-primary-50"
                    : "border-background-200 bg-background-50 hover:border-primary-200 hover:bg-background-100"
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center border ${
                    qtype === "single" ? "rounded-full" : "rounded"
                  } ${selected ? "border-primary-500" : "border-background-400"}`}
                  aria-hidden="true"
                >
                  {selected && (
                    <i
                      className={`${
                        qtype === "single"
                          ? "ri-checkbox-blank-circle-fill"
                          : "ri-checkbox-fill"
                      } text-[13px] text-primary-600`}
                    ></i>
                  )}
                </span>
                <span
                  className={`text-sm ${
                    selected
                      ? "font-semibold text-foreground-950"
                      : "text-foreground-700"
                  }`}
                >
                  {opt.text}
                </span>
              </button>
            );
          })}
          {limitHit && (
            <p className="flex items-center gap-1.5 text-xs font-medium text-accent-700">
              <i className="ri-error-warning-line" aria-hidden="true"></i>
              최대 {maxPick}개까지 선택할 수 있습니다.
            </p>
          )}
        </div>
      )}

      {qtype === "text" && (
        <div className="mt-3">
          <textarea
            value={value.text}
            onChange={(e) =>
              onChange({ ...value, text: e.target.value.slice(0, 2000) })
            }
            maxLength={2000}
            rows={5}
            placeholder="의견을 입력해 주세요."
            className="w-full resize-none rounded-lg border border-background-200 bg-background-50 p-3 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          <div className="mt-1.5 text-right text-xs text-foreground-400">
            {value.text.length} / 2000
          </div>
        </div>
      )}

      {qtype === "scale" && (
        <div className="mt-3">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((s) => {
              const selected = value.score === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => onChange({ ...value, score: s })}
                  aria-pressed={selected}
                  className={`h-12 flex-1 rounded-lg border text-base font-bold transition-colors ${
                    selected
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-200 hover:bg-background-100"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between text-xs text-foreground-500">
            <span>전혀 아니다</span>
            <span>매우 그렇다</span>
          </div>
        </div>
      )}
    </li>
  );
}